import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import api from "../services/api";
import {
  createProcurementOrder,
  listProcurementOrders,
  listSuppliers,
} from "../services/procurementService";
import ProcurementOrderForm from "../components/procurement/ProcurementOrderForm";
import ProcurementOrderStatusBadge from "../components/procurement/ProcurementOrderStatusBadge";
import ProcurementOrderAssociationBadge from "../components/procurement/ProcurementOrderAssociationBadge";
import procurementErrorMessage from "../components/procurement/procurementErrorMessage";

const INITIAL_FILTERS = {
  type: "",
  status: "",
  associationType: "",
  needsLink: "",
  supplier: "",
  createdBy: "",
  from: "",
  to: "",
  search: "",
};

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("nl-NL");
}

export default function ProcurementOrdersPage() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [showForm, setShowForm] = useState(false);
  const [preferTuFlow, setPreferTuFlow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);

  const load = useCallback(async (nextFilters) => {
    try {
      setLoading(true);
      setError("");
      const [ordersRes, suppliersRes, clientsRes, projectsRes, workOrdersRes] = await Promise.all([
        listProcurementOrders(nextFilters),
        listSuppliers(),
        api.get("/clients"),
        api.get("/projects"),
        api.get("/werkbonnen"),
      ]);
      setOrders(Array.isArray(ordersRes?.items) ? ordersRes.items : []);
      setSuppliers(Array.isArray(suppliersRes) ? suppliersRes : []);
      setClients(Array.isArray(clientsRes?.data) ? clientsRes.data : []);
      setProjects(Array.isArray(projectsRes?.data) ? projectsRes.data : []);
      setWorkOrders(Array.isArray(workOrdersRes?.data) ? workOrdersRes.data : []);
    } catch (err) {
      setError(procurementErrorMessage(err, "Inkooporders laden mislukt."));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initiale load; filters worden handmatig via submit toegepast.
  useEffect(() => {
    load(INITIAL_FILTERS);
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const wantsNew = ["1", "true", "yes"].includes(
      String(params.get("new") || "").trim().toLowerCase()
    );
    const wantsTu = ["1", "true", "yes"].includes(
      String(params.get("tu") || "").trim().toLowerCase()
    );
    if (wantsNew) {
      setShowForm(true);
    }
    if (wantsTu) {
      setShowForm(true);
      setPreferTuFlow(true);
    }
  }, [location.search]);

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
  };

  const submitFilter = (event) => {
    event.preventDefault();
    load(filters);
  };

  const handleCreate = async (payload) => {
    setSubmitting(true);
    try {
      await createProcurementOrder(payload);
      setShowForm(false);
      await load(filters);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Logboek24</p>
            <h1 className="text-2xl font-semibold text-slate-900">Inkooporders</h1>
            <p className="text-sm text-slate-600">Materiaalbestellingen en service-opdrachten aan externen in 1 overzicht.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/pl/inkoop/verwerking" className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700">
              Verwerkingsview
            </Link>
            <button
              type="button"
              onClick={() => {
                setPreferTuFlow(true);
                setShowForm(true);
              }}
              className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-800"
            >
              Open OCI catalogus
            </button>
            <button
              type="button"
              onClick={() => {
                setPreferTuFlow(false);
                setShowForm(true);
              }}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              {showForm && preferTuFlow ? "Ga verder zonder OCI" : "Nieuwe inkooporder"}
            </button>
          </div>
        </div>
      </section>

      {showForm && (
        <ProcurementOrderForm
          tuRequested={preferTuFlow}
          suppliers={suppliers}
          clients={clients}
          projects={projects}
          workOrders={workOrders}
          onCancel={() => setShowForm(false)}
          onSubmit={handleCreate}
          submitting={submitting}
        />
      )}

      <form onSubmit={submitFilter} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-4">
          <select value={filters.type} onChange={(e) => updateFilter("type", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Alle types</option>
            <option value="material_order">material_order</option>
            <option value="service_order">service_order</option>
          </select>
          <select value={filters.status} onChange={(e) => updateFilter("status", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Alle statussen</option>
            <option value="concept">concept (Concept)</option>
            <option value="requested">requested (Ingediend)</option>
            <option value="under_review">under_review (In beoordeling)</option>
            <option value="approval_pending">approval_pending (In beoordeling)</option>
            <option value="approved">approved (Goedgekeurd)</option>
            <option value="approved_for_ordering">approved_for_ordering (Goedgekeurd)</option>
            <option value="ordered">ordered (Besteld)</option>
            <option value="partially_received">partially_received (Deels geleverd)</option>
            <option value="received">received (Geleverd)</option>
            <option value="rejected">rejected (Afgewezen)</option>
            <option value="closed">closed (Geleverd)</option>
            <option value="cancelled">cancelled (Afgewezen)</option>
            <option value="sent">sent (legacy)</option>
            <option value="confirmed">confirmed (legacy)</option>
            <option value="in_progress">in_progress (legacy)</option>
            <option value="completed">completed (legacy)</option>
            <option value="besteld">besteld</option>
            <option value="opgehaald">opgehaald</option>
            <option value="ontvangen">ontvangen</option>
            <option value="nog_te_koppelen">nog_te_koppelen</option>
            <option value="gekoppeld">gekoppeld</option>
            <option value="gecontroleerd">gecontroleerd</option>
          </select>
          <select value={filters.associationType} onChange={(e) => updateFilter("associationType", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Alle koppelingen</option>
            <option value="workorder">Werkbon</option>
            <option value="project">Project</option>
            <option value="voorraad_magazijn">Voorraad / magazijn</option>
            <option value="nog_te_koppelen">Nog te koppelen</option>
          </select>
          <select value={filters.needsLink} onChange={(e) => updateFilter("needsLink", e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Nog te koppelen: alle</option>
            <option value="true">Ja</option>
            <option value="false">Nee</option>
          </select>
          <input
            value={filters.supplier}
            onChange={(e) => updateFilter("supplier", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Leverancier"
          />
          <input
            value={filters.createdBy}
            onChange={(e) => updateFilter("createdBy", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Besteller"
          />
          <input
            type="date"
            value={filters.from}
            onChange={(e) => updateFilter("from", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={filters.to}
            onChange={(e) => updateFilter("to", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          <input
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            placeholder="Zoek op titel, ordernr, leverancier..."
          />
          <button type="submit" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
            Filter toepassen
          </button>
        </div>
      </form>

      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Hoort bij</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Leverancier / Resource</th>
                <th className="px-4 py-2">Context</th>
                <th className="px-4 py-2">Datum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!loading && orders.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-slate-500" colSpan={7}>Nog geen inkooporders gevonden.</td>
                </tr>
              )}
              {orders.map((order) => (
                <tr key={order._id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/pl/inkoop/${order._id}`} className="font-medium text-slate-900 hover:underline">
                      {order.orderNumber || "PO"}
                    </Link>
                    <p className="text-xs text-slate-500">{order.title}</p>
                  </td>
                  <td className="px-4 py-2 text-slate-700">{order.type}</td>
                  <td className="px-4 py-2">
                    <ProcurementOrderAssociationBadge associationType={order.associationType} />
                  </td>
                  <td className="px-4 py-2">
                    <ProcurementOrderStatusBadge
                      status={order.status}
                      displayStatus={order.displayStatus || order.displayStatusKey}
                      label={order.displayStatusLabel}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <p className="text-slate-800">{order.supplierName || "-"}</p>
                    {order.type === "service_order" && (
                      <p className="text-xs text-slate-500">{order.resourceType || "-"} {order.resourceName ? `· ${order.resourceName}` : ""}</p>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    <p>Klant: {order.clientName || "-"}</p>
                    <p>Project: {order.projectName || "-"}</p>
                    <p>Werkbon: {order.workOrderTitle || "-"}</p>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{formatDate(order.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
